import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

import { clientQueryKeys } from "./query-keys";

import { vacancyQueryKeys } from "@/features/vacancies/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";
import { PaymentTypeEnum } from "@/types/client/ClientType";
import {
  EditClientErrorResponse,
  EditClientResponse,
} from "@/types/response/EditClientResponse";
import { GetClientResponse } from "@/types/response/GetClientResponse";
import { GetClientsResponse } from "@/types/response/GetClientsResponse";

type UpdateContractDetailsRequest = {
  paymentType?: PaymentTypeEnum;
  isSelfService?: boolean;
  tenantId?: string | null;
};

type MutationContext = {
  previousClient?: GetClientResponse;
  previousClientsLists?: Map<string, GetClientsResponse>;
};

export function useUpdateContractDetailsMutation(
  clientId?: string,
  tenantId?: string | null
) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    AxiosResponse<EditClientResponse>,
    AxiosError<EditClientErrorResponse>,
    UpdateContractDetailsRequest,
    MutationContext
  >({
    mutationFn: (request: UpdateContractDetailsRequest) =>
      instance.put(`/api/v2/Clients/${clientId}/contract-details`, {
        paymentType: request.paymentType,
        isSelfService: request.isSelfService,
        tenantId: request.tenantId || tenantId,
      }),
    onMutate: async (updatedData) => {
      if (!clientId) return { previousClient: undefined };

      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: clientQueryKeys.detail(clientId),
      });
      await queryClient.cancelQueries({
        queryKey: clientQueryKeys.all,
      });

      // Snapshot previous values
      const previousClient = queryClient.getQueryData<GetClientResponse>(
        clientQueryKeys.detail(clientId)
      );

      // Snapshot all clients list queries and optimistically update them
      // List queries: ["clients", clientType, descending, orderBy, page, pageSize, paymentType, search, tenantId]
      // Detail queries: ["clients", "detail", clientId]
      // Filters queries: ["clients", "filters", ...]
      const previousClientsLists = new Map<string, GetClientsResponse>();
      const allQueries = queryClient.getQueryCache().getAll();
      const clientsListQueries = allQueries.filter(
        (q) =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === "clients" &&
          q.queryKey.length > 1 &&
          q.queryKey[1] !== "filters" && // Exclude filters queries
          !(q.queryKey.length === 2 && typeof q.queryKey[1] === "string") // Exclude detail queries
      );

      for (const query of clientsListQueries) {
        const queryKey = query.queryKey;
        const queryKeyStr = JSON.stringify(queryKey);
        const previousData =
          queryClient.getQueryData<GetClientsResponse>(queryKey);
        if (previousData) {
          previousClientsLists.set(queryKeyStr, previousData);
          // Optimistically update the list
          queryClient.setQueryData<GetClientsResponse>(queryKey, (old) => {
            if (!old?.data) return old;
            return {
              ...old,
              data: old.data.map((client) =>
                client.id2 === clientId
                  ? {
                    ...client,
                    paymentType:
                        updatedData.paymentType ?? client.paymentType,
                    isSelfService:
                        updatedData.isSelfService ?? client.isSelfService,
                  }
                  : client
              ),
            };
          });
        }
      }

      // Optimistically update client detail
      if (previousClient) {
        queryClient.setQueryData<GetClientResponse>(
          clientQueryKeys.detail(clientId),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              paymentType: updatedData.paymentType ?? old.paymentType,
              isSelfService: updatedData.isSelfService ?? old.isSelfService,
            };
          }
        );
      }

      // Return context with snapshots
      return { previousClient, previousClientsLists };
    },
    onError: (err, _variables, context) => {
      // Rollback on error
      if (context?.previousClient && clientId) {
        queryClient.setQueryData<GetClientResponse>(
          clientQueryKeys.detail(clientId),
          context.previousClient
        );
      }

      // Rollback all clients list queries
      if (context?.previousClientsLists) {
        for (const [
          queryKeyStr,
          previousData,
        ] of context.previousClientsLists) {
          const queryKey = JSON.parse(queryKeyStr);
          queryClient.setQueryData<GetClientsResponse>(queryKey, previousData);
        }
      }

      // Show error toast
      const errorMessage =
        err.response?.data && Array.isArray(err.response.data)
          ? err.response.data[0]?.errorMessage || "An error occurred"
          : "Failed to update contract details";
      showToast(errorMessage, "error");
    },
    onSuccess: () => {
      // Invalidate all client queries to ensure consistency
      // Use refetchType: 'active' to force immediate refetch of active queries
      queryClient.invalidateQueries({
        queryKey: clientQueryKeys.all,
        refetchType: "active",
      });
      // Invalidate specific client detail if we have clientId
      if (clientId) {
        queryClient.invalidateQueries({
          queryKey: clientQueryKeys.detail(clientId),
        });
      }
      // Invalidate vacancies as they may depend on client data
      queryClient.invalidateQueries({
        queryKey: vacancyQueryKeys.all,
      });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      if (clientId) {
        queryClient.invalidateQueries({
          queryKey: clientQueryKeys.detail(clientId),
        });
      }
    },
  });
}
